import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CustomPricingState } from "./custom-pricing.entity";
import { fetchServices, fetchCustomPricing, saveCustomPricing, fetchProperty } from "./custom-pricing.api";
import { uuid } from "@/interfaces";
import type { PropertyPriceSetting } from "@/repositories/vendor-property-service-pricing.repository";
import { ApiRequestError } from "@shyne/shared/api";

const PROPERTY_NOT_FOUND = "not_found";

export const getServices = createAsyncThunk("custom-pricing/getServices", fetchServices);

export const getCustomPricing = createAsyncThunk(
  "custom-pricing/getCustomPricing",
  async ({ propertyId, vendorId }: { propertyId: uuid; vendorId: uuid }) => fetchCustomPricing(propertyId, vendorId)
);

export const saveCustomPricingBatch = createAsyncThunk(
  "custom-pricing/saveBatch",
  async ({ propertyId, vendorId, prices }: { propertyId: uuid; vendorId: uuid; prices: PropertyPriceSetting[] }) => saveCustomPricing(propertyId, vendorId, prices)
);

export const getProperty = createAsyncThunk("custom-pricing/getProperty", async (propertyId: uuid, { rejectWithValue }) => {
  try {
    return await fetchProperty(propertyId);
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) return rejectWithValue(PROPERTY_NOT_FOUND);
    throw error;
  }
});

const initialState: CustomPricingState = {
  list: [],
  customPricing: [],
  customPricingRequestId: null,
  customPricingLoadedFor: null,
  customPricingFailed: false,
  property: null,
  loading: false,
  customPricingLoading: false,
  propertyLoading: false,
  propertyNotFound: false,
  total: 0,
  selectedPricing: null,
  hasUnsavedChanges: false,
};

const customPricingSlice = createSlice({
  name: "customPricing",
  initialState,
  reducers: {
    setSelectedPricing: (state, action) => {
      state.selectedPricing = action.payload;
    },
    setHasUnsavedChanges: (state, action) => {
      state.hasUnsavedChanges = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServices.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServices.fulfilled, (state, action) => {
        state.list = action.payload.data;
        state.total = action.payload.count;
        state.loading = false;
      })
      .addCase(getServices.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getCustomPricing.pending, (state, action) => {
        state.customPricing = [];
        state.customPricingRequestId = action.meta.requestId;
        state.customPricingLoadedFor = null;
        state.customPricingFailed = false;
        state.customPricingLoading = true;
        state.hasUnsavedChanges = false;
      })
      .addCase(getCustomPricing.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.customPricingRequestId) return;
        state.customPricing = action.payload;
        state.customPricingLoadedFor = action.meta.arg.propertyId;
        state.customPricingLoading = false;
      })
      .addCase(getCustomPricing.rejected, (state, action) => {
        if (action.meta.requestId !== state.customPricingRequestId) return;
        state.customPricingFailed = true;
        state.customPricingLoading = false;
      })
      .addCase(saveCustomPricingBatch.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveCustomPricingBatch.fulfilled, (state, action) => {
        state.loading = false;
        if (state.customPricingLoadedFor !== action.meta.arg.propertyId) return;
        state.customPricing = action.payload;
        state.hasUnsavedChanges = false;
      })
      .addCase(saveCustomPricingBatch.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getProperty.pending, (state) => {
        state.property = null;
        state.propertyNotFound = false;
        state.propertyLoading = true;
      })
      .addCase(getProperty.fulfilled, (state, action) => {
        state.property = action.payload;
        state.propertyLoading = false;
      })
      .addCase(getProperty.rejected, (state, action) => {
        state.propertyNotFound = action.payload === PROPERTY_NOT_FOUND;
        state.propertyLoading = false;
      });
  },
});

export const pricesLoadedFor = (state: CustomPricingState, propertyId: uuid | undefined) =>
  propertyId !== undefined && state.customPricingLoadedFor === propertyId && !state.customPricingLoading;

export const { setSelectedPricing, setHasUnsavedChanges } = customPricingSlice.actions;

export default customPricingSlice.reducer;
